import { Input, Output } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { debounceTime } from 'rxjs';
import { FilterSearchingRechazosTracesViewModel } from '../rechazostraces.types';
import { ComponentBase } from '../../../../core/_infrastructure/classes/componentBase';
import { EnumSiNo, Tc } from '../../../../core/_infrastructure/types/common';
import { GlobalService } from '../../../../core/_infrastructure/classes/services/global.service';
import { NotificationService } from '../../../../core/_infrastructure/classes/services/notification.service';
import { AuthService } from '../../../../core/_infrastructure/classes/services/auth.service';
import { ExceptionManager } from '../../../../core/_infrastructure/classes/handlers/exceptionManager';
import { CustomValidator } from '../../../../core/_infrastructure/classes/customValidator';

@Component({
    selector: 'app-rechazostraces-filter',
    templateUrl: './rechazostraces-filter.component.html',
})
export class RechazosTracesFilterComponent extends ComponentBase implements OnInit {
    @Output() search = new EventEmitter<FilterSearchingRechazosTracesViewModel>();
    @Output() cleanForm = new EventEmitter<any>(); // Informa que el formulario se ha reseteado
    @Input() lastFilter: FilterSearchingRechazosTracesViewModel;
    @Input() listPaises: Tc[];
    @Input() isRechazo = false;

    listSiNo: SelectItem[] = [
        { label: 'Sí', value: EnumSiNo.SI },
        { label: 'NO', value: EnumSiNo.NO },
    ];

    firstDayMonth: Date;
    lastDayMonth: Date;

    public isCollapsed = false;

    constructor(
        private fb: FormBuilder,
        private globalService: GlobalService,
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.firstDayMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        const temp = new Date().getMonth() + 1;
        this.lastDayMonth = new Date(new Date(new Date().getFullYear(), temp, 1).getTime() - 0.001 * 60 * 60 * 24);

        this.createForm();
        this.recoverLastSearch();
    }

    private createForm(): void {
        this.form = this.fb.group({
            PaisOrigen: null,
            PaisDestino: null,
            PaisEmision: null,
            CodigoTaric: ['', CustomValidator.CodigoTaric],
            BusquedaExacta: EnumSiNo.NO,
            FechaDecisionDesde: [this.firstDayMonth, Validators.required],
            FechaDecisionHasta: [this.lastDayMonth, Validators.required],
            IsRechazo: this.isRechazo,
        });
    }

    private recoverLastSearch(): void {
        if (this.lastFilter) {
            // Asignar valores al formulario de manera más eficiente
            this.form.setValue({
                PaisOrigen: this.lastFilter.PaisOrigen,
                PaisDestino: this.lastFilter.PaisDestino,
                PaisEmision: this.lastFilter.PaisEmision,
                CodigoTaric: this.lastFilter.CodigoTaric,
                BusquedaExacta: this.lastFilter.BusquedaExacta,
                FechaDecisionDesde: this.lastFilter.FechaDecisionDesde,
                FechaDecisionHasta: this.lastFilter.FechaDecisionHasta,
                IsRechazo: this.lastFilter.IsRechazo,
            });

            // Marcar el formulario como modificado
            this.form.markAsDirty();

            // Ejecutar búsqueda con un retraso
            setTimeout(() => {
                this.onSearch(null);
            }, 200);
        }

        // Manejo de cambios en el formulario con debounce
        this.form.valueChanges.pipe(debounceTime(500)).subscribe(values => {
            if (values) {
                this.clearPaginationFilter();
            }
        });
    }

    resetForm($event: any): void {
        $event.stopPropagation();
        this.form.setValue({
            PaisOrigen: null,
            PaisDestino: null,
            PaisEmision: null,
            CodigoTaric: '',
            BusquedaExacta: EnumSiNo.NO,
            FechaDecisionDesde: this.firstDayMonth,
            FechaDecisionHasta: this.lastDayMonth,
            IsRechazo: this.isRechazo,
        });
        this.form.markAsPristine();

        this.searchingButton = false;
        this.isCollapsed = false;

        this.cleanForm.emit();
    }

    private clearPaginationFilter(): void {
        if (!this.lastFilter) return;

        this.lastFilter.PageNumber = 0;
        this.lastFilter.Asc = 0;
        this.lastFilter.OrderBy = '';
    }

    onSearch($event: any): void {
        if (!this.form.valid) {
            return;
        }

        if (
            this.form.value.FechaDecisionDesde != null &&
            this.form.value.FechaDecisionHasta != null &&
            this.form.value.FechaDecisionDesde > this.form.value.FechaDecisionHasta
        ) {
            this.addErrorMessage('La Fecha Decisión Inicio no puede ser posterior a la Fecha Decisión Hasta');
            return;
        }

        $event?.stopPropagation();
        this.searchingButton = true;
        this.isCollapsed = true;

        // Determinar cuál payload usar dependiendo de si es Rechazo o no
        const payload: FilterSearchingRechazosTracesViewModel = {
            ...this.form.value,
            //TODO emarti69: Cuando TRACES "arregle" la variable TotalCount, descomentar la línea de debajo para que la última búsqueda realizada no refresque siempre a la página 1.
            PageNumber: /* this.lastFilter?.PageNumber ||*/ 0,
            Asc: this.lastFilter?.Asc,
            OrderBy: this.lastFilter?.OrderBy || '',
        };

        // Emitir el payload correspondiente
        this.search.emit(payload);
    }

    formIsUntouched(): boolean {
        return this.form.pristine || !this.form.dirty;
    }

    /**
     * Getter para controlar la propiedad disabled del botón de búsqueda.
     */
    get disableSearchBtn(): boolean {
        return this.isCollapsed || !this.listPaises || this.listPaises?.length == 0;
    }
}
